/**
 * AgentConnectors — the composer's connectors control, the way Claude shows
 * connectors in a chat: a small trigger that opens a compact panel listing
 * each connector with its logo, its name, a status line and a switch, and a
 * quiet chip beside it while one is on.
 *
 * Google Drive only for now. The agent's editor ALLOWS Drive; each person
 * turns it on or off for THIS agent here, for themselves — OFF by default (or
 * on through Settings → "Turn on Google Drive automatically"). Turning it on
 * while not connected takes them through Google's consent in a popup first
 * (the `basic` level; reading their own Drive stays "asked when needed").
 *
 * Renders NOTHING where the agent does not allow Drive or the tenant has it
 * off (the server's answer, `allowed`, is the last word) AND the agent has no
 * connector each person signs in to. No disabled clutter.
 *
 * Connectors each person signs in to (item 65A) are listed under Drive, the
 * same row look: the person's own connection, Connect / Disconnect — the
 * agent then acts as them on their turns.
 *
 * Styling: the trigger is the composer's own icon button (ModelSelector.css);
 * the panel's row speaks the agent editor's Connectors-tab language — the
 * icon tile, the card with its "on" tint, the accent pill — in design tokens.
 */

import { memo, useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Switch } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { useTenantPath } from '@/routing';
import { GoogleDriveGlyph } from '../drive/driveIcons';
import {
  cachedAgentDriveSwitch,
  forgetAgentDriveSwitch,
  getAgentDriveSwitch,
  setAgentDriveSwitch,
  subscribeAgentDriveSwitches,
  type AgentDriveSwitch,
} from '@/modules/cloud-storage/services/agentDriveSwitchService';
import {
  requestGoogleAccess,
  type AccessRequest,
  type GoogleAccessOutcome,
} from '@/modules/cloud-storage/services/googleAccessService';
import { myConnectorsService, type MyConnector } from '@/modules/connector_library/services/myConnectorsService';
import { PersonalConnect } from '@/modules/connector_library/components/PersonalConnect';
import { hangFromInlineEnd } from './panelSide';
import '../ModelSelector.css';
import './AgentConnectors.css';

interface AgentConnectorsProps {
  chatbotId: string;
  /** The agent allows Google Drive under the tenant flag. */
  driveAllowed: boolean;
  /** The open conversation: how a project member reaches the project
   *  thread's agent when they cannot reach it directly. */
  conversationId?: string;
}

type Busy = 'connecting' | 'saving' | null;

const OUTCOME_NOTICE: Partial<Record<GoogleAccessOutcome, string>> = {
  blocked: 'chat.connectors.popupBlocked',
  declined: 'chat.connectors.notConnectedResult',
  cancelled: 'chat.connectors.notConnectedResult',
  wrong_account: 'chat.connectors.failed',
  error: 'chat.connectors.failed',
};

function AgentConnectorsImpl({ chatbotId, driveAllowed, conversationId }: AgentConnectorsProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const [state, setState] = useState<AgentDriveSwitch | undefined>(() => cachedAgentDriveSwitch(chatbotId));
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<Busy>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Which edge of the trigger the panel hangs from — measured at open time, as
  // AttachMenu does: the toolbar's tools sit at the same physical edge in both
  // directions, so a logical inset alone could push the panel off the composer.
  const [alignEnd, setAlignEnd] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const switchRef = useRef<HTMLButtonElement>(null);
  const request = useRef<AccessRequest | null>(null);
  const titleId = useId();
  const statusId = useId();
  // The agent's connectors each person signs in to, with THIS person's connection.
  const [personal, setPersonal] = useState<MyConnector[]>([]);
  const loadPersonal = useCallback(() => {
    myConnectorsService.list({ chatbotId, conversationId })
      .then(setPersonal)
      .catch(() => setPersonal([]));
  }, [chatbotId, conversationId]);
  useEffect(() => { loadPersonal(); }, [loadPersonal]);

  // The shared answer for this agent; refreshed whenever any composer changes
  // it, the Google connection changes, or the automatic preference does.
  useEffect(() => {
    let alive = true;
    if (!driveAllowed) { setState({ allowed: false }); return () => { alive = false; }; }
    const load = () => {
      getAgentDriveSwitch(chatbotId, conversationId)
        .then((next) => { if (alive) setState(next); })
        .catch(() => { if (alive) setState({ allowed: false }); });
    };
    load();
    const unsubscribe = subscribeAgentDriveSwitches(load);
    return () => { alive = false; unsubscribe(); };
  }, [chatbotId, conversationId, driveAllowed]);

  // A Google window still open when the composer goes away is abandoned.
  useEffect(() => () => request.current?.cancel(), []);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    setNotice(null);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    // Focus moves into the panel, onto its first control.
    const frame = requestAnimationFrame(() => {
      if (switchRef.current) switchRef.current.focus();
      else rootRef.current?.querySelector<HTMLElement>('.agent-connectors__panel button')?.focus();
    });
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close(true);
      }
    };
    // A non-modal popover: Tab out of it and it closes, as a click outside does.
    const root = rootRef.current;
    const onFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (next && root && !root.contains(next)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    root?.addEventListener('focusout', onFocusOut);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      root?.removeEventListener('focusout', onFocusOut);
    };
  }, [open, close]);

  // Drive's own answer, when the agent allows it (narrowed for the reads below).
  const on = state && state.allowed ? state : null;
  const drive = on !== null;
  if (!drive && personal.length === 0) return null;

  const toggleOpen = () => {
    const root = rootRef.current;
    if (root) {
      const rtl = getComputedStyle(root).direction === 'rtl';
      setAlignEnd(hangFromInlineEnd(root.getBoundingClientRect(), window.innerWidth, rtl));
    }
    if (open) close(false);
    else setOpen(true);
  };

  const save = async (enabled: boolean) => {
    setBusy('saving');
    try {
      setState(await setAgentDriveSwitch(chatbotId, enabled, conversationId));
    } catch (err) {
      // 409: the agent stopped allowing Drive, or the connection expired —
      // the panel was out of date, so it is read again rather than blamed.
      if ((err as { response?: { status?: number } })?.response?.status === 409) forgetAgentDriveSwitch(chatbotId);
      else setNotice('chat.connectors.failed');
    } finally {
      setBusy(null);
    }
  };

  const onSwitch = (next: boolean) => {
    if (busy) return;
    setNotice(null);
    if (!on) return;
    if (next && !on.connected) {
      // Connect first. Synchronously, inside the click: a popup opened after
      // an await is blocked.
      const req = requestGoogleAccess('basic');
      request.current = req;
      setBusy('connecting');
      void req.outcome.then((outcome) => {
        request.current = null;
        if (outcome === 'granted') {
          void save(true);
          return;
        }
        setBusy(null);
        setNotice(OUTCOME_NOTICE[outcome] ?? 'chat.connectors.failed');
      });
      return;
    }
    void save(next);
  };

  const active = !!on?.active;
  // The account is one unit: its own element (bidi-isolated inside a Hebrew
  // sentence) that is never broken — it moves to the next line whole.
  const account = on?.account_email || '';
  let status: ReactNode = t('chat.connectors.notConnected');
  if (busy === 'connecting') status = t('chat.connectors.connecting');
  else if (on?.connected) {
    status = (
      <Trans
        i18nKey="chat.connectors.connectedAs"
        values={{ email: account }}
        components={{ account: <bdi className="agent-connectors__account" title={account} /> }}
      />
    );
  }

  return (
    <div className="agent-connectors" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`model-selector-trigger agent-connectors__trigger ${open ? 'open' : ''}`}
        onClick={toggleOpen}
        aria-label={t('chat.connectors.trigger')}
        title={t('chat.connectors.trigger')}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="agent-connectors-trigger"
      >
        {/* A two-pronged plug: "connectors". */}
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 2v5" />
          <path d="M15 2v5" />
          <path d="M6 7h12v4a6 6 0 0 1-12 0Z" />
          <path d="M12 17v5" />
        </svg>
      </button>

      {active && (
        <button
          type="button"
          className="agent-connectors__chip"
          onClick={toggleOpen}
          aria-label={t('chat.connectors.chipLabel')}
          title={t('chat.connectors.chipLabel')}
          data-testid="agent-connectors-drive-chip"
        >
          <GoogleDriveGlyph size={13} />
          <span>{t('chat.connectors.chip')}</span>
        </button>
      )}

      {open && (
        <div
          className={`agent-connectors__panel ${alignEnd ? 'agent-connectors__panel--end' : ''}`}
          role="dialog"
          aria-labelledby={titleId}
          data-testid="agent-connectors-panel"
        >
          <div className="agent-connectors__title" id={titleId}>{t('chat.connectors.title')}</div>

          {on && (
          <div className={`agent-connectors__row ${active ? 'is-on' : ''}`} data-testid="agent-connectors-drive">
            <span className="agent-connectors__icon" aria-hidden="true"><GoogleDriveGlyph size={20} /></span>
            <span className="agent-connectors__body">
              <span className="agent-connectors__name">{t('chat.connectors.googleDrive')}</span>
              <span className="agent-connectors__status" id={statusId} data-testid="agent-connectors-drive-status">
                {status}
              </span>
              {/* Google's window may be closed without an answer, which the
                  popup helper cannot see (COOP): the person ends the wait. */}
              {busy === 'connecting' && (
                <button type="button" className="agent-connectors__link"
                        onClick={() => request.current?.cancel()} data-testid="agent-connectors-cancel">
                  {t('chat.connectors.cancel')}
                </button>
              )}
              {!on.connected && busy !== 'connecting' && (
                <span className="agent-connectors__hint">{t('chat.connectors.connectHint')}</span>
              )}
              {active && on.source === 'auto' && (
                <span className="agent-connectors__hint">{t('chat.connectors.autoOn')}</span>
              )}
              {active && (
                <span className="agent-connectors__pill">{t(`bots.connectors.level.${on.level}`)}</span>
              )}
            </span>
            <Switch
              ref={switchRef}
              checked={active}
              onChange={onSwitch}
              aria-busy={busy !== null}
              aria-label={t('chat.connectors.switchLabel')}
              aria-describedby={statusId}
              data-testid="agent-connectors-drive-switch"
            />
          </div>
          )}

          {personal.map(c => (
            <div key={c.entry_id} className={`agent-connectors__row ${c.status === 'connected' ? 'is-on' : ''}`}>
              <PersonalConnect connector={c} variant="compact" onChanged={loadPersonal} />
            </div>
          ))}

          {notice && <p className="agent-connectors__notice" role="status">{t(notice)}</p>}

          <div className="agent-connectors__foot">
            <span>{t('chat.connectors.forYouOnly')}</span>
            <button
              type="button"
              className="agent-connectors__link"
              onClick={() => { close(false); navigate(tenantPath('/settings')); }}
            >
              {t('chat.connectors.settingsLink')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Memoized: the composer re-renders on every keystroke. */
export const AgentConnectors = memo(AgentConnectorsImpl);
